import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid,  Button as Butt, TextField, InputAdornment, Alert, Checkbox } from "@mui/material";
import React, { useState, useCallback } from "react"
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks"
import { ProcedureUpdate } from "../../../../../services/verifyService/commands";
import { demoteProcedure, promoteProcedure, removeProcedure, updateProcedure } from "../../../../../services/verifyService/model/verification/VerificationSlice";
import { ReduxNestedUpdate } from "../../../../../utils/ReduxUpdateCommands";

// Editable currency field that formats on blur and allows free-form typing
function CurrencyField({value, name, priority, onChange, disabled}: {value: number, name: string, priority: number, onChange: (priority: string, name: string, val: number) => void, disabled?: boolean}){
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState('');

    const displayValue = editing ? localValue : value.toFixed(2);

    const handleFocus = () => {
        setEditing(true);
        setLocalValue(value === 0 ? '' : value.toString());
    };

    const handleBlur = () => {
        setEditing(false);
        const parsed = parseFloat(localValue);
        const val = isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
        onChange(priority.toString(), name, val);
    };

    return <TextField name={name} value={displayValue} size='small' disabled={disabled}
        onChange={(e) => { if(editing) setLocalValue(e.target.value); }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        sx={disabled ? { backgroundColor:'#F0F0F0', '& legend': { display: 'none' }, '& fieldset': { top: 0 }} : { '& legend': { display: 'none' }, '& fieldset': { top: 0 }}} fullWidth
        InputProps={{startAdornment:<InputAdornment sx={{backgroundColor:'#E9ECEF'}} position='start'>$</InputAdornment>}}/>
}

function CptCodes(){
    const dispatch = useAppDispatch();
    const verification = useAppSelector((state)=> state.verification.verification);

    const handleCurrencyChange = useCallback((priority: string, name: string, val: number) => {
        dispatch(updateProcedure(new ReduxNestedUpdate(priority, name, val)));
    }, [dispatch]);

    const handleReimbursementChange = useCallback((priority: string, name: string, val: number) => {
        const proc = verification.procedures.find(p => p.priority.toString() === priority);
        if(!proc) return;
        dispatch(updateProcedure(new ReduxNestedUpdate(priority, name, val)));
        const total = name === 'procedureReimbursement' ? val + proc.implantReimbursement : proc.procedureReimbursement + val;
        dispatch(updateProcedure(new ReduxNestedUpdate(priority, 'reimbursement', Math.round(total * 100) / 100)));
    }, [dispatch, verification.procedures]);

    return <React.Fragment>
        {verification.procedures.length === 0 &&
            <Alert className='ms-2 me-2 mt-2' color="warning">No Procedure Codes have been added to this case.</Alert>
        }
        {verification.procedures.length > 0 && 
        <Grid container spacing={1} marginLeft='5px'>
            <Grid item sm={1.75}><h6>Actions</h6></Grid>
            <Grid item sm={0.75}><h6>Priority</h6></Grid>
            <Grid item sm={0.75}><h6>Code</h6></Grid>
            <Grid item sm={2}><h6>Description</h6></Grid>
            <Grid item sm={0.9}><h6>Bill Amt</h6></Grid>
            <Grid item sm={0.9}><h6>Proc Reimb</h6></Grid>
            <Grid item sm={0.9}><h6>Impl Reimb</h6></Grid>
            <Grid item sm={0.9}><h6>Total Reimb</h6></Grid>
            <Grid item sm={0.9}><h6>Impl Cost</h6></Grid>
            <Grid item sm={0.6}><h6>Self Pay</h6></Grid>
            <Grid item sm={0.6}><h6>Auth Req</h6></Grid>
        </Grid>
        }
        {verification.procedures.map(i=>(
            <Grid container spacing={1} sx={{mb:1}} marginLeft='5px' key={i.priority}>
                <Grid item sm={1.75}>
                    <Butt color='error' variant='outlined' size='large' sx={{height:'40px',mr:0.5}} onClick={()=>dispatch(removeProcedure(i.priority))}><FontAwesomeIcon icon='minus-circle'/></Butt>
                    <Butt variant='outlined' size='large' sx={{height:'40px',mr:0.5}} onClick={()=>dispatch(promoteProcedure(i.priority))} disabled={i.priority === 1}><FontAwesomeIcon icon='arrow-up'/></Butt>
                    <Butt variant='outlined' size='large' sx={{height:'40px'}} onClick={()=>dispatch(demoteProcedure(i.priority))} disabled={i.priority >= verification.procedures.length}><FontAwesomeIcon icon='arrow-down'/></Butt>
                </Grid>
                <Grid item sm={0.75}>
                    <TextField value={i.priority} size='small' InputLabelProps={{shrink:false}} sx={{backgroundColor:'#E9ECEF', '& legend': { display: 'none' }, '& fieldset': { top: 0 },}} fullWidth/>
                </Grid>
                <Grid item sm={0.75}>
                    <TextField value={i.code} size='small' InputLabelProps={{shrink:false}} sx={{backgroundColor:'#E9ECEF', '& legend': { display: 'none' }, '& fieldset': { top: 0 },}} fullWidth/>
                </Grid>
                <Grid item sm={2}>
                    <TextField value={i.description} size='small' InputLabelProps={{shrink:false}} sx={{backgroundColor:'#E9ECEF', '& legend': { display: 'none' }, '& fieldset': { top: 0 },}} fullWidth/>
                </Grid>
                <Grid item sm={0.9}>
                    <CurrencyField value={i.billAmount} name='billAmount' priority={i.priority} onChange={handleCurrencyChange}/>
                </Grid>
                <Grid item sm={0.9}>
                    <CurrencyField value={i.procedureReimbursement} name='procedureReimbursement' priority={i.priority} onChange={handleReimbursementChange}/>
                </Grid>
                <Grid item sm={0.9}>
                    <CurrencyField value={i.implantReimbursement} name='implantReimbursement' priority={i.priority} onChange={handleReimbursementChange}/>
                </Grid>
                <Grid item sm={0.9}>
                    <CurrencyField value={i.reimbursement} name='reimbursement' priority={i.priority} onChange={handleCurrencyChange} disabled={true}/>
                </Grid>
                <Grid item sm={0.9}>
                    <CurrencyField value={i.implantCost} name='implantCost' priority={i.priority} onChange={handleCurrencyChange}/>
                </Grid>
                <Grid item sm={0.6}>
                    <Checkbox size='small' name='selfPay' value={i.selfPay} checked={i.selfPay} onChange={(e)=>dispatch(updateProcedure(new ReduxNestedUpdate(i.priority.toString(),'selfPay',e.target.checked)))}/>
                </Grid>
                <Grid item sm={0.6}>
                    <Checkbox size='small' name='isAuthRequired' value={i.isAuthRequired} checked={i.isAuthRequired} onChange={(e)=>dispatch(updateProcedure(new ReduxNestedUpdate(i.priority.toString(),'isAuthRequired',e.target.checked)))}/>
                </Grid>
            </Grid>
        ))}
        
    </React.Fragment> 

}

export {CptCodes}